/**
 * Hooks Configuration API Client
 * Matches endpoints from api/routers/admin/hook_configs.py
 */

import { apiClient } from './client';
import type {
  HookPipeline,
  HookConfig,
  HookConfigUpdate,
  HookReorderItem,
  HookOperationResponse,
} from '../../types/hooks';

export const hooksApi = {
  /**
   * Get the full hook pipeline for an agent
   * GET /v1/admin/hooks/agents/{agent_type}/pipeline
   */
  getPipeline: (agentType: string, userId?: string) =>
    apiClient.get<HookPipeline>(`/hooks/agents/${agentType}/pipeline`, {
      params: userId ? { user_id: userId } : undefined,
    }),

  /**
   * Get effective configuration for a specific hook
   * GET /v1/admin/hooks/agents/{agent_type}/hooks/{hook_name}
   */
  getHookConfig: (agentType: string, hookName: string, userId?: string) =>
    apiClient.get<HookConfig>(`/hooks/agents/${agentType}/hooks/${hookName}`, {
      params: userId ? { user_id: userId } : undefined,
    }),

  /**
   * Update configuration for a specific hook (partial update)
   * PUT /v1/admin/hooks/agents/{agent_type}/hooks/{hook_name}
   */
  updateHook: (
    agentType: string,
    hookName: string,
    data: HookConfigUpdate,
    userId?: string
  ) =>
    apiClient.put<HookConfig>(
      `/hooks/agents/${agentType}/hooks/${hookName}`,
      data,
      {
        params: userId ? { user_id: userId } : undefined,
      }
    ),

  /**
   * Reorder hooks for an agent (drag-and-drop support)
   * PUT /v1/admin/hooks/agents/{agent_type}/reorder
   */
  reorder: (
    agentType: string,
    hookOrders: HookReorderItem[],
    userId?: string
  ) =>
    apiClient.put<HookPipeline>(
      `/hooks/agents/${agentType}/reorder`,
      { hook_orders: hookOrders },
      {
        params: userId ? { user_id: userId } : undefined,
      }
    ),

  /**
   * Delete an agent hook configuration (revert to defaults)
   * DELETE /v1/admin/hooks/agents/{agent_type}/hooks/{hook_name}
   */
  resetHook: (agentType: string, hookName: string, userId?: string) =>
    apiClient.delete<HookOperationResponse>(
      `/hooks/agents/${agentType}/hooks/${hookName}`,
      {
        params: userId ? { user_id: userId } : undefined,
      }
    ),

  /**
   * Reset all hook configurations for an agent to defaults
   * DELETE /v1/admin/hooks/agents/{agent_type}/hooks
   */
  resetAll: (agentType: string, userId?: string) =>
    apiClient.delete<HookOperationResponse>(
      `/hooks/agents/${agentType}/hooks`,
      {
        params: userId ? { user_id: userId } : undefined,
      }
    ),
};
